import { queryOptions } from "@tanstack/react-query";

import { apiPaths, workItemPath, workItemSchema, workItemsPageSchema, type WorkItemState } from "@ravnar/api-contract";

import { ApiError, apiRequest } from "./client";
import { LIST_STALE_TIME_MS } from "./refresh";

// The Work page's requests. Every query key starts with "work", so completing a party — which closes its item —
// refreshes the list, the drawer and the rail's badge at once.

/**
 * How often the first page of open items is asked again: the rail's badge counts it on every page of the app, and the
 * Directory agent raises items behind the page's back, seconds to minutes after a document is filed.
 */
export const OPEN_WORK_REFETCH_INTERVAL_MS = 60_000;

export interface WorkItemsPageRequest {
	/** The open items, which the badge counts, unless the Done tab asks for the others. */
	readonly state?: WorkItemState;
	/** A page link from an earlier page; the first page when there is none. */
	readonly page?: string;
}

/**
 * A page of the work items the member may see, in one state, with the count of the whole state and links to the pages
 * beside it. The open items' first page is asked for again every minute, because nothing the page does tells it when
 * the Directory agent raised another; no other page is polled.
 */
export const workItemsQueryOptions = ({ state = "open", page }: WorkItemsPageRequest = {}) =>
	queryOptions({
		queryKey: ["work", "page", { state, page }],
		queryFn: ({ signal }) =>
			apiRequest(apiPaths.workItems, workItemsPageSchema, {
				query: { state, ...(page !== undefined && { page }) },
				signal,
			}),
		staleTime: LIST_STALE_TIME_MS,
		refetchInterval: state === "open" && page === undefined ? OPEN_WORK_REFETCH_INTERVAL_MS : false,
		// A refused page link will not change on a retry, and the page redirects on it straight away.
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
	});

/**
 * One work item, open or done: what the drawer opens with. The API answers an item the member may not see as it
 * answers one that does not exist, with 404, and the drawer then stays shut.
 */
export const workItemQueryOptions = (workItemId: string) =>
	queryOptions({
		queryKey: ["work", "item", workItemId],
		queryFn: ({ signal }) => apiRequest(workItemPath(workItemId), workItemSchema, { signal }),
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
	});
